import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDashboardStore } from '../stores/dashboardStore'
import { useEmployees } from '../hooks/useEmployees'
import { useOrderTemplates } from '../hooks/useOrderTemplates'

interface Chip {
  key: string
  label: string
  muted?: boolean
  onRemove: () => void
}

function formatDateLabel(from: string | null, to: string | null): string {
  // Show friendly label for known presets
  if (from && !to) {
    const diffYears = Math.round(
      (Date.now() - new Date(from).getTime()) / (365.25 * 24 * 60 * 60 * 1000)
    )
    if (diffYears >= 1 && diffYears <= 5) {
      return `Siste ${diffYears} år`
    }
  }
  const parts: string[] = []
  if (from) parts.push(`fra ${from}`)
  if (to) parts.push(`til ${to}`)
  return parts.join(' ')
}

export function ActiveFilterChips() {
  const store = useDashboardStore()
  const { data: employees = [] } = useEmployees()
  const { data: templates = [] } = useOrderTemplates()

  const chips: Chip[] = []

  // Status is handled by StatusTabs — no chip needed

  // Ansatt
  if (store.employeeFilter.length > 0) {
    const names = store.employeeFilter
      .map((id) => employees.find((e) => e.employee_id === id)?.full_name ?? `#${id}`)
      .join(', ')
    chips.push({
      key: 'employee',
      label: `Ansatt: ${names}`,
      onRemove: () => store.setEmployeeFilter([]),
    })
  }

  // Ordremal
  if (store.templateFilter.length > 0) {
    const names = store.templateFilter
      .map((id) => templates.find((t) => t.order_template_id === id)?.name ?? `#${id}`)
      .join(', ')
    chips.push({
      key: 'template',
      label: `Ordremal: ${names}`,
      onRemove: () => store.setTemplateFilter([]),
    })
  }

  // Ordredato
  if (store.orderDateFrom || store.orderDateTo) {
    chips.push({
      key: 'orderDate',
      label: `Dato: ${formatDateLabel(store.orderDateFrom, store.orderDateTo)}`,
      onRemove: () => store.setDateRange('orderDate', null, null),
    })
  }

  // Ordrenr range
  if (store.orderIdFrom !== null || store.orderIdTo !== null) {
    const parts: string[] = []
    if (store.orderIdFrom !== null) parts.push(`fra ${store.orderIdFrom}`)
    if (store.orderIdTo !== null) parts.push(`til ${store.orderIdTo}`)
    chips.push({
      key: 'orderIdRange',
      label: `Ordrenr: ${parts.join(' ')}`,
      onRemove: () => store.setOrderIdRange(null, null),
    })
  }

  // Leveringsdato
  if (store.deliveryDateFrom || store.deliveryDateTo) {
    chips.push({
      key: 'deliveryDate',
      label: `Lev.dato: ${formatDateLabel(store.deliveryDateFrom, store.deliveryDateTo)}`,
      onRemove: () => store.setDateRange('deliveryDate', null, null),
    })
  }

  // Text filters
  const textFilters: { column: 'customerName' | 'reference' | 'yourRef' | 'info' | 'phone' | 'email' | 'customerId' | 'deliveryName'; label: string; value: string }[] = [
    { column: 'customerName', label: 'Kunde', value: store.customerNameFilter },
    { column: 'reference', label: 'Merking', value: store.referenceFilter },
    { column: 'yourRef', label: 'Kundens ref', value: store.yourRefFilter },
    { column: 'info', label: 'Ordreinfo', value: store.infoFilter },
    { column: 'phone', label: 'Tlf', value: store.phoneFilter },
    { column: 'email', label: 'E-post', value: store.emailFilter },
    { column: 'customerId', label: 'Kundenr', value: store.customerIdFilter },
    { column: 'deliveryName', label: 'Lev.navn', value: store.deliveryNameFilter },
  ]
  for (const f of textFilters) {
    if (f.value.length > 0) {
      chips.push({
        key: f.column,
        label: `${f.label}: ${f.value}`,
        onRemove: () => store.setTextFilter(f.column, ''),
      })
    }
  }

  if (chips.length === 0) return null

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className={cn(
            'inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full max-w-[220px]',
            chip.muted
              ? 'bg-blue-50/80 text-blue-500 border border-blue-100'
              : 'bg-gray-100 text-gray-700'
          )}
        >
          <span className="truncate">{chip.label}</span>
          <button
            onClick={chip.onRemove}
            className="flex-shrink-0 hover:text-gray-900 transition-colors"
          >
            <X size={11} />
          </button>
        </span>
      ))}
    </div>
  )
}
